import { Box, Drawer as MUIDrawer } from '@mui/material';
import { DrawerProps } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import React, { createElement, Fragment, useState } from 'react';
import { wrapper } from './Drawer.styles';
import TAGSvgIcon from '../TAGSvgIcon/TAGSvgIcon';
export type Anchor = 'top' | 'left' | 'bottom' | 'right';
export interface IDrawer extends DrawerProps {
  anchor: Anchor;
  children?: React.ReactNode;
  closeButton?: boolean;
  height?: string;
  onClick?: React.MouseEventHandler<HTMLUListElement> & React.MouseEventHandler<HTMLDivElement>;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onClose?: (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void;
  open?: boolean;
  sheetVariant?: 'outlined' | 'elevation';
  triggerComponent?: React.ReactNode;
  width?: string;
  roundedTop?: boolean;
  fullScreen?: boolean;
}
type TriggerDivWrapperProps = {
  anchor: Anchor;
  onClick?: React.MouseEventHandler<HTMLUListElement> & React.MouseEventHandler<HTMLDivElement>;
  toggleDrawer: (anchor: Anchor, open: boolean) => () => void;
  triggerComponent?: React.ReactNode;
};
function TriggerDivWrapper({
  anchor,
  onClick,
  toggleDrawer,
  triggerComponent
}: Readonly<TriggerDivWrapperProps>) {
  return createElement('div', {
    onClick: onClick ?? toggleDrawer(anchor, true),
    anchor,
    style: {
      cursor: 'pointer',
      width: 'fit-content',
      height: 'fit-content'
    },
    'data-test': 'testingDiv'
  }, triggerComponent);
}
export default function Drawer({
  anchor,
  children,
  closeButton = false,
  height,
  onClick,
  onClose,
  open,
  sheetVariant = 'outlined',
  triggerComponent,
  width,
  roundedTop = false,
  fullScreen = false,
  PaperProps,
  ...props
}: Readonly<IDrawer>) {
  const {
    sx: paperSx,
    ...restPaperProps
  } = PaperProps ?? {};
  const paperSxOverrides = paperSx && !Array.isArray(paperSx) && typeof paperSx !== 'function' ? paperSx : {};
  const [position, setPosition] = useState({
    top: false,
    left: false,
    bottom: false,
    right: false
  });
  const toggleDrawer = (anchor: Anchor, open: boolean) => () => {
    setPosition({
      ...position,
      [anchor]: open
    });
  };
  return <Box data-testid="section_drawer" sx={wrapper} data-sentry-element="Box" data-sentry-component="Drawer" data-sentry-source-file="Drawer.tsx">
			<Fragment key={anchor}>
				<MUIDrawer {...props} PaperProps={{
        variant: sheetVariant,
        ...restPaperProps,
        sx: {
          borderRadius: roundedTop ? '0.625rem 0.625rem 0 0' : null,
          maxWidth: anchor === 'top' || anchor === 'bottom' ? 'auto' : '80%',
          maxHeight: anchor === 'top' || anchor === 'bottom' ? '80%' : 'auto',
          minWidth: 240,
          margin: '2px',
          width,
          height,
          ...(fullScreen && {
            maxWidth: '100%',
            maxHeight: '100%'
          }),
          ...paperSxOverrides
        }
      }} data-test="testDrawer" anchor={anchor} elevation={sheetVariant === 'elevation' ? 5 : 0} open={open ?? position[anchor]} onClose={onClose ?? toggleDrawer(anchor, false)} data-sentry-element="MUIDrawer" data-sentry-source-file="Drawer.tsx">
					<div data-test="testLayout" style={{
          display: 'flex',
          alignItems: 'center'
        }}>
						{closeButton && <IconButton onClick={onClose ?? toggleDrawer(anchor, false)} sx={{
            position: 'absolute',
            right: 1,
            top: 20,
            zIndex: 3,
            color: 'secondary.main'
          }} aria-label="close" data-testid="drawer_close_button">
								<TAGSvgIcon icon="Times" size={28} />
							</IconButton>}
					</div>
					{React.Children.map(children, child => React.isValidElement(child) &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.cloneElement(child as React.ReactElement<any>, {
          onClose: onClose ?? toggleDrawer(anchor, false)
        }))}
				</MUIDrawer>
				<TriggerDivWrapper anchor={anchor} onClick={onClick} toggleDrawer={toggleDrawer} triggerComponent={triggerComponent} data-sentry-element="TriggerDivWrapper" data-sentry-source-file="Drawer.tsx" />
			</Fragment>
		</Box>;
}